import React, { lazy } from 'react';

// project import
import MainLayout from 'layout/MainLayout';
import Loadable from 'components/Loadable';
import AdminGuard from 'utils/route-guard/AdminGuard';
import { Navigate } from 'react-router';

// pages maintenance pages
const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));

// render - main-pages
const Organizations = Loadable(lazy(() => import('pages/main-pages/Organizations')));
const CreateOrg = Loadable(lazy(() => import('pages/main-pages/CreateOrg')));
const ViewEditOrg = Loadable(lazy(() => import('pages/main-pages/ViewEditOrg')));
const TabInfo = Loadable(lazy(() => import('sections/view-edit-org/TabInfo')));
const TabUsers = Loadable(lazy(() => import('sections/view-edit-org/TabUsers')));
const TabPanels = Loadable(lazy(() => import('sections/view-edit-org/TabPanels')));
const TabModules = Loadable(lazy(() => import('sections/view-edit-org/TabModules')));
const TabCitationTemplate = Loadable(
    lazy(() => import('sections/view-edit-org/TabCitationTemplate'))
);
const TabShiftReporting = Loadable(
    lazy(() => import('sections/view-edit-org/TabShiftReporting'))
);
const TabNotifications = Loadable(
    lazy(() => import('sections/view-edit-org/TabNotifications'))
);
const TabBroadcasts = Loadable(
    lazy(() => import('sections/view-edit-org/TabBroadcasts'))
);
const TabStations = Loadable(lazy(() => import('sections/view-edit-org/TabStations')));
const TabMobileCredentials = Loadable(
    lazy(() => import('sections/view-edit-org/TabMobileCredentials'))
);
const TabIdentityVerification = Loadable(
    lazy(() => import('sections/view-edit-org/TabIdentityVerification'))
);
const TabLprLicenseConfiguration = Loadable(
    lazy(() => import('sections/view-edit-org/TabLprLicenseConfiguration'))
);
const TabLprAccess = Loadable(lazy(() => import('sections/view-edit-org/TabLprAccess')));
const TabManagerLimit = Loadable(
    lazy(() => import('sections/view-edit-org/TabManagerLimit'))
);
const TabAssociateAppAccess = Loadable(
    lazy(() => import('sections/view-edit-org/TabAssociateAppAccess'))
);
const TabCa4kConfig = Loadable(
    lazy(() => import('sections/view-edit-org/TabCa4kConfig'))
);
const AnalsysTabPanel = Loadable(
    lazy(() => import('components/Analysis/AnalsysTabPanel'))
);
const TabDeviceFacilityCodes = Loadable(
    lazy(() => import('sections/view-edit-org/TabDeviceFacilityCodes'))
);
const Property = Loadable(lazy(() => import('pages/main-pages/Property')));
const TabProperties = Loadable(
    lazy(() => import('sections/view-edit-org/TabProperties'))
);
const TabRentalManagement = Loadable(
    lazy(() => import('sections/view-edit-org/TabRentalManagement'))
);
const TabSharingAgreements = Loadable(
    lazy(() => import('sections/view-edit-org/TabSharingAgreements'))
);
const Reports = Loadable(lazy(() => import('pages/main-pages/Reports')));
const SharingAgreements = Loadable(
    lazy(() => import('pages/main-pages/SharingAgreements'))
);
const LprInspection = Loadable(lazy(() => import('pages/main-pages/LprInspection')));

const VirtualGuards = Loadable(
    lazy(() => import('pages/super_admin/virtual_guards/VirtualGuardsPage'))
);
const AccountDeletion = Loadable(
    lazy(() => import('pages/super_admin/account_deletion/AccountDeletionPage'))
);
const HealthDashboard = Loadable(
    lazy(() => import('pages/super_admin/health_dashboard/HealthDashboardPage'))
);
const HealthDeviceDetails = Loadable(
    lazy(() => import('pages/super_admin/health_dashboard/HealthDeviceDetailsPage'))
);
const AppVersions = Loadable(
    lazy(() => import('pages/super_admin/app_versions/AppVersionsPage'))
);

// ==============================|| ADMIN ROUTING ||============================== //

const AdminRoutes = {
    path: '/',
    children: [
        {
            path: '*',
            element: <MaintenanceError />
        },
        {
            path: '/',
            element: (
                <AdminGuard>
                    <MainLayout />
                </AdminGuard>
            ),
            children: [
                {
                    path: 'orgs',
                    element: <Organizations />
                },
                {
                    path: 'orgs/:orgId',
                    element: <ViewEditOrg />,
                    children: [
                        {
                            path: 'org-info',
                            element: <TabInfo />
                        },
                        {
                            path: 'org-users',
                            element: <TabUsers />
                        },
                        {
                            path: 'broadcasts',
                            element: <TabBroadcasts />
                        },
                        {
                            path: 'org-modules',
                            element: <TabModules />
                        },
                        {
                            path: 'citation-template',
                            element: <TabCitationTemplate />
                        },
                        {
                            path: 'ca4k-config',
                            element: <TabCa4kConfig />
                        },
                        {
                            path: 'org-stations',
                            element: <TabStations />
                        },
                        {
                            path: 'org-kiosks',
                            element: <TabPanels />
                        },
                        {
                            path: 'org-properties',
                            element: <TabProperties />
                        },
                        {
                            path: 'access-control',
                            element: <AnalsysTabPanel />
                        },
                        {
                            path: 'device-facility-code',
                            element: <TabDeviceFacilityCodes />
                        },
                        {
                            path: 'mobile-credentials',
                            element: <TabMobileCredentials />
                        },
                        {
                            path: 'user-identification',
                            element: <TabIdentityVerification />
                        },
                        {
                            path: 'shift-reporting',
                            element: <TabShiftReporting />
                        },
                        {
                            path: 'notifications',
                            element: <TabNotifications />
                        },
                        {
                            path: 'lpr-license-configuration',
                            element: <TabLprLicenseConfiguration />
                        },
                        {
                            path: 'lpr-access',
                            element: <TabLprAccess />
                        },
                        {
                            path: 'manager-limit',
                            element: <TabManagerLimit />
                        },
                        {
                            path: 'associate-app-access',
                            element: <TabAssociateAppAccess />
                        },
                        {
                            path: 'org-properties/:id',
                            element: <Property />
                        },
                        {
                            path: 'rental-management',
                            element: <TabRentalManagement />
                        },
                        {
                            path: 'org-sharing-agreements',
                            element: <TabSharingAgreements />
                        },
                        { path: '*', element: <Navigate to="org-info" replace /> }
                    ]
                },
                {
                    path: 'create-org',
                    element: <CreateOrg />
                },
                {
                    path: 'reports',
                    element: <Reports />
                },
                {
                    path: 'sharing-agreements',
                    element: <SharingAgreements />
                },
                {
                    path: 'lpr-inspection',
                    element: <LprInspection />
                },
                {
                    path: 'virtual-guards',
                    element: <VirtualGuards />
                },
                {
                    path: 'account-deletion',
                    element: <AccountDeletion />
                },
                {
                    path: 'health-dashboard',
                    element: <HealthDashboard />
                },
                {
                    path: 'health-dashboard/:key',
                    element: <HealthDeviceDetails />
                },
                {
                    path: 'app-versions',
                    element: <AppVersions />
                }
            ]
        }
    ]
};

export default AdminRoutes;
